/**
 * @file ChatWindowProvider.tsx
 * @description Провайдер окна «Сообщения» кабинета магазина
 * (chat-window-design.md §6, §8; SC64).
 *
 * Живёт в `ShopLayout` над `<Outlet/>`: переход между страницами окно не
 * пересоздаёт. Каркас монтирует провайдер с `key` магазина и входа под
 * сотрудником — смена магазина или начало/конец входа под сотрудником
 * пересоздают окно (прежние запросы прерываются размонтированием, окно
 * закрыто). Состояние — `sessionStorage` вкладки (`chat-window-state.ts`);
 * ключи чужого сотрудника в той же вкладке стираются при старте, при 401
 * и при явном выходе (`AuthProvider.logout`).
 *
 * Здесь же опрос `/admin/chat/summary` (`useChatSummary`): число
 * непрочитанных диалогов нужно и кнопке, и «Диалог не выбран».
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";

import type { ChatTab } from "../../lib/api-chat";
import { AUTH_UNAUTHENTICATED_EVENT } from "../../lib/api-client";
import { ChatWindowContext, type ChatWindowContextValue, type OpenChatTarget } from "./chat-window-context";
import {
  chatPresentation,
  chatStorageKeys,
  chatWindowReducer,
  clearChatSession,
  parseChatState,
  serializeChatState,
  type ChatCustomerFilter,
} from "./chat-window-state";
import { useChatHistorySteps } from "./useChatHistorySteps";
import { useChatSummary } from "./useChatSummary";
import { useChatUrlParam } from "./useChatUrlParam";
import { useChatViewport } from "./useChatViewport";
import { useOpenChatResolver } from "./useOpenChatResolver";

interface Props {
  enabled: boolean;
  shopId: string;
  slug: string;
  userId: string;
  readOnly: boolean;
  canDispatch: boolean;
  canOpenOrders: boolean;
  canOpenCustomers: boolean;
  children: ReactNode;
}

/** Начальное состояние: своё у вкладки; другой сотрудник — с чистого листа. */
function initialState(shopId: string, userId: string, readOnly: boolean) {
  const store = window.sessionStorage;
  if (store.getItem(chatStorageKeys.owner()) !== userId) {
    clearChatSession(store);
    store.setItem(chatStorageKeys.owner(), userId);
  }
  const state = parseChatState(store.getItem(chatStorageKeys.state(shopId)));
  // Под сотрудником окно при входе закрыто (§6).
  return readOnly ? { ...state, open: false } : state;
}

export function ChatWindowProvider(props: Props) {
  const { enabled, shopId, slug, userId, readOnly, canDispatch, canOpenOrders, canOpenCustomers, children } = props;
  const [state, dispatch] = useReducer(chatWindowReducer, undefined, () => initialState(shopId, userId, readOnly));
  const viewport = useChatViewport();
  const presentation = useMemo(() => chatPresentation(viewport, state.expanded), [viewport, state.expanded]);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const [summaryTick, setSummaryTick] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const resolve = useOpenChatResolver(readOnly);
  const summary = useChatSummary(enabled, state.open, summaryTick, readOnly);
  const narrow = viewport === "phone" || viewport === "tablet";

  useEffect(() => {
    window.sessionStorage.setItem(chatStorageKeys.state(shopId), serializeChatState(state));
  }, [shopId, state]);

  // Сессия кончилась (401) — ключи чата вкладки стираются (§6 «выход»).
  // Явный выход другим сотрудником ловит метка владельца в `initialState`.
  useEffect(() => {
    const onSignedOut = () => clearChatSession();
    window.addEventListener(AUTH_UNAUTHENTICATED_EVENT, onSignedOut);
    return () => window.removeEventListener(AUTH_UNAUTHENTICATED_EVENT, onSignedOut);
  }, []);

  const steps = useChatHistorySteps(narrow, {
    onClosed: () => dispatch({ type: "close" }),
    onList: () => dispatch({ type: "open", selectedId: null }),
    onConversation: (id) => dispatch({ type: "open", selectedId: id }),
  });

  const openChat = useCallback(
    (target: OpenChatTarget = {}) => {
      if (!enabled) return;
      setNotice(null);
      const direct = Boolean(target.conversationId || target.customerId);
      if (!direct) {
        dispatch({ type: "open" });
        steps.pushWindow();
        return;
      }
      void resolve(target).then((resolved) => {
        setNotice(resolved.notice ?? null);
        dispatch({ type: "open", selectedId: resolved.selectedId, customer: resolved.customer });
        if (resolved.selectedId) steps.pushConversation(resolved.selectedId);
        else steps.pushWindow();
      });
    },
    [enabled, resolve, steps],
  );

  const close = useCallback(() => {
    if (!steps.closeSteps()) dispatch({ type: "close" });
    // Фокус — на кнопку, когда она снова появится.
    window.setTimeout(() => launcherRef.current?.focus(), 0);
  }, [steps]);

  const select = useCallback(
    (id: string | null) => {
      if (id) setNotice(null);
      dispatch({ type: "select", id });
      if (id) steps.pushConversation(id);
    },
    [steps],
  );

  const back = useCallback(() => {
    if (!steps.backToList()) dispatch({ type: "select", id: null });
  }, [steps]);

  useChatUrlParam(enabled, openChat);

  const value = useMemo<ChatWindowContextValue>(
    () => ({
      enabled,
      shopId,
      slug,
      userId,
      readOnly,
      canDispatch,
      canOpenOrders,
      canOpenCustomers,
      state,
      viewport,
      presentation,
      openChat,
      close,
      hide: () => dispatch({ type: "close" }),
      setExpanded: (expanded: boolean) => dispatch({ type: "setExpanded", expanded }),
      select,
      back,
      setTab: (tab: ChatTab) => dispatch({ type: "tab", tab }),
      setUnassigned: (v: boolean) => dispatch({ type: "unassigned", value: v }),
      setCustomer: (customer: ChatCustomerFilter | null) => dispatch({ type: "customer", customer }),
      setSearch: (v: string) => dispatch({ type: "search", value: v }),
      launcherRef,
      summaryTick,
      refreshSummary: () => setSummaryTick((n) => n + 1),
      dataVersion,
      bumpData: () => {
        setDataVersion((n) => n + 1);
        setSummaryTick((n) => n + 1);
      },
      unread: summary.unread,
      hint: summary.hint,
      dismissHint: summary.dismissHint,
      notice,
      dismissNotice: () => setNotice(null),
    }),
    [enabled, shopId, slug, userId, readOnly, canDispatch, canOpenOrders, canOpenCustomers, state, viewport, presentation, openChat, close, select, back, dataVersion, summaryTick, summary.unread, summary.hint, summary.dismissHint, notice],
  );

  return <ChatWindowContext.Provider value={value}>{children}</ChatWindowContext.Provider>;
}
